import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ScheduleAiImport } from "@/components/ScheduleAiImport";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SchedulePending } from "@/components/SchedulePending";
import { CalendarClock, FileDown, UploadCloud, Save, Loader2, Plus, AlertTriangle, Pencil, Printer, FileText } from "lucide-react";

const NONE = "__none__";

function SlotDialog({ slot, ctx, onClose, onApply }) {
  const [f, setF] = useState(slot || {});
  useEffect(() => setF(slot || {}), [slot]);
  if (!slot) return null;
  const match = ctx.teachers.find((t) => (t.registration || "").toLowerCase() === (f.teacher_registration || "").trim().toLowerCase());
  const setReg = (v) => {
    const t = ctx.teachers.find((x) => (x.registration || "").toLowerCase() === v.trim().toLowerCase());
    setF((p) => ({ ...p, teacher_registration: v, teacher_name: t ? t.name : p.teacher_name }));
  };
  const period = ctx.periods.find((p) => p.period === slot.period);
  const day = ctx.days.find((d) => d.day === slot.day);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="slot-dialog">
        <DialogHeader>
          <DialogTitle>{day?.label} • {slot.period}º tempo</DialogTitle>
          <DialogDescription>{period?.start} às {period?.end}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>ID Funcional do Professor</Label>
            <Input value={f.teacher_registration || ""} onChange={(e) => setReg(e.target.value)} placeholder="Ex.: MAT-2201" data-testid="slot-teacher-registration" />
            {f.teacher_registration && f.teacher_registration !== "999999" && !match && <p className="mt-1 flex items-center gap-1 text-xs text-amber-700" data-testid="slot-teacher-not-found"><AlertTriangle className="h-3 w-3" />ID não encontrado no cadastro de Professores</p>}
            <p className="mt-1 text-[11px] text-slate-400">Deixe em branco ou use 999999 para tempo sem professor.</p>
          </div>
          <div><Label>Nome do Professor</Label>
            <Input value={f.teacher_name || ""} onChange={(e) => setF((p) => ({ ...p, teacher_name: e.target.value }))} disabled={!!match} data-testid="slot-teacher-name" />
          </div>
          <div><Label>Disciplina</Label>
            <Select value={f.subject_id || NONE} onValueChange={(v) => setF((p) => ({ ...p, subject_id: v === NONE ? null : v }))}>
              <SelectTrigger data-testid="slot-subject-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— Sem disciplina —</SelectItem>
                {ctx.subjects.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`slot-subject-${s.id}`}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onApply({ day: slot.day, period: slot.period })} data-testid="slot-clear-btn">Limpar tempo</Button>
          <Button onClick={() => onApply({ ...f, teacher_name: match ? match.name : f.teacher_name, subject_name: ctx.subjects.find((s) => s.id === f.subject_id)?.name })} className="bg-orange-500 hover:bg-orange-600" data-testid="slot-apply-btn">Aplicar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Grid({ ctx, slots, canEdit, onEdit }) {
  const get = (d, p) => slots.find((s) => s.day === d && s.period === p);
  return (
    <div className="overflow-x-auto" data-testid="schedule-grid">
      <table className="w-full min-w-[860px] border-separate border-spacing-1 text-sm">
        <thead>
          <tr>
            <th className="w-28 rounded-md bg-slate-800 px-2 py-2 text-left text-xs font-semibold text-white">Tempo</th>
            {ctx.days.map((d) => <th key={d.day} className="rounded-md bg-slate-800 px-2 py-2 text-xs font-semibold text-white">{d.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {ctx.periods.map((p) => (
            <tr key={p.period}>
              <td className="rounded-md bg-slate-100 px-2 py-2 align-top">
                <div className="font-semibold text-slate-800">{p.period}º</div>
                <div className="text-[11px] text-slate-500">{p.start} – {p.end}</div>
              </td>
              {ctx.days.map((d) => {
                const s = get(d.day, p.period);
                const vacant = s && s.teacher_registration === "999999";
                return (
                  <td key={d.day} className="align-top">
                    <button type="button" disabled={!canEdit} onClick={() => onEdit(s || { day: d.day, period: p.period })}
                      data-testid={`slot-${d.day}-${p.period}`}
                      className={`group h-full min-h-[72px] w-full rounded-md border p-2 text-left transition-colors ${vacant ? "border-red-200 bg-red-50 hover:bg-red-100" : s ? "border-orange-200 bg-orange-50 hover:bg-orange-100" : "border-dashed border-slate-200 bg-white hover:bg-slate-50"} disabled:cursor-default`}>
                      {s ? (
                        <>
                          <div className="font-semibold leading-tight text-slate-900">{s.subject_name || <span className="text-amber-700">Sem disciplina</span>}</div>
                          <div className={`mt-0.5 text-xs ${vacant ? "font-medium text-red-700" : "text-slate-600"}`}>{s.teacher_name || "—"}</div>
                          <div className="text-[10px] text-slate-400">ID {s.teacher_registration || "—"}{s.teacher_registration && !s.teacher_id && !vacant ? " • não cadastrado" : ""}</div>
                        </>
                      ) : canEdit && <span className="flex items-center gap-1 text-xs text-slate-400 group-hover:text-orange-600"><Plus className="h-3 w-3" />Preencher</span>}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function QuadroHorarios() {
  const { scopeParams, selectedSchoolId } = useAuth();
  const [params, setParams] = useSearchParams();
  const [classes, setClasses] = useState([]);
  const [shifts, setShifts] = useState([]);
  const classId = params.get("turma") || "";
  const [ctx, setCtx] = useState(null);
  const [slots, setSlots] = useState([]);
  const [shift, setShift] = useState("MANHA");
  const [editing, setEditing] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [statusKey, setStatusKey] = useState(0);
  const [canEdit, setCanEdit] = useState(false);
  useEffect(() => { if (ctx) setCanEdit(!!ctx.can_edit); }, [ctx]);

  useEffect(() => { api.get("/schedule/shifts").then((r) => setShifts(r.data)); }, []);
  useEffect(() => {
    api.get("/classes", { params: scopeParams }).then((r) => {
      setClasses(r.data);
      if (!classId && r.data[0]) setParams({ turma: r.data[0].id }, { replace: true });
    });
    // eslint-disable-next-line
  }, [selectedSchoolId]);

  const load = useCallback(() => {
    if (!classId) return;
    setCtx(null);
    api.get(`/classes/${classId}/schedule`).then((r) => { setCtx(r.data); setSlots(r.data.slots); setShift(r.data.shift); setDirty(false); setWarnings([]); setStatusKey((k) => k + 1); })
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  }, [classId]);
  useEffect(() => { load(); }, [load]);

  const periods = (ctx && shift === ctx.shift ? ctx.periods : null) || shifts.find((s) => s.key === shift)?.periods || ctx?.periods || [];
  const view = ctx ? { ...ctx, periods } : null;

  const apply = (s) => {
    setSlots((prev) => {
      const rest = prev.filter((x) => !(x.day === s.day && x.period === s.period));
      return s.subject_id || s.teacher_registration || s.teacher_name ? [...rest, s] : rest;
    });
    setDirty(true); setEditing(null);
  };
  const changeShift = (v) => {
    setShift(v); setDirty(true);
    const max = shifts.find((s) => s.key === v)?.periods.length || 10;
    setSlots((prev) => prev.filter((s) => s.period <= max));
  };
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/classes/${classId}/schedule`, { shift, slots });
      toast.success(`Quadro salvo: ${r.data.saved} tempo(s) preenchido(s)`);
      load(); setWarnings(r.data.warnings);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const getPdf = async (mode) => {
    if (dirty) { toast.info("Salve as alterações antes de gerar o PDF"); return; }
    setBusy(true);
    try {
      const r = await api.get(`/classes/${classId}/schedule/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (mode === "print") {
        const w = window.open(url, "_blank");
        if (!w) toast.error("Permita pop-ups para imprimir");
        else w.addEventListener("load", () => w.print());
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = `quadro_horarios_turma_${ctx?.class?.name || ""}.pdf`; a.click();
      }
    } catch { toast.error("Esta turma ainda não tem quadro de horários salvo"); } finally { setBusy(false); }
  };
  const getSchoolPdf = async () => {
    setBusy(true);
    try {
      const r = await api.get("/schedule/school/pdf", { params: { school_id: selectedSchoolId || undefined }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      a.download = "quadros_horarios_escola.pdf"; a.click();
      toast.success("PDF com os quadros de todas as turmas gerado");
    } catch { toast.error("Nenhuma turma desta escola tem quadro de horários salvo"); } finally { setBusy(false); }
  };
  const download = async () => {
    const r = await api.get(`/classes/${classId}/schedule/template`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = `quadro_horarios_${ctx?.class?.name || "turma"}.xlsx`; a.click();
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/classes/${classId}/schedule/import`, fd, { params: { shift } });
      toast.success(`Planilha importada: ${r.data.imported} tempo(s)`);
      load(); setWarnings(r.data.warnings);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="quadro-horarios-page">
      <PageHeader title="Quadro de Horários" subtitle="Tempos por turno, de segunda a sexta, com professor e disciplina. A presença do quiosque é registrada em cada tempo.">
        {canEdit && (
          <ScheduleAiImport schoolId={selectedSchoolId} onDone={load} />
        )}
        {ctx?.can_edit && (
          <Button onClick={getSchoolPdf} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="schedule-school-pdf-btn">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4" />}Todas as turmas (PDF)
          </Button>
        )}
      </PageHeader>
      <Card className="mb-4 grid min-w-0 grid-cols-1 gap-3 p-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
        <div className="space-y-1.5"><Label>Turma</Label>
          <Select value={classId} onValueChange={(v) => setParams({ turma: v })}>
            <SelectTrigger data-testid="schedule-class-select"><SelectValue placeholder="Selecione a turma" /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`schedule-class-${c.id}`}>{c.name}{c.shift ? ` • ${c.shift}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Turno</Label>
          <Select value={shift} onValueChange={changeShift} disabled={!ctx?.can_edit}>
            <SelectTrigger data-testid="schedule-shift-select"><SelectValue /></SelectTrigger>
            <SelectContent>{shifts.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`schedule-shift-${s.key}`}>{s.label} ({s.periods.length} tempos)</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {ctx?.can_edit && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => getPdf("download")} disabled={busy || !slots.length} data-testid="schedule-pdf-download-btn"><FileText className="mr-1.5 h-4 w-4" />Baixar PDF</Button>
            <Button variant="outline" onClick={() => getPdf("print")} disabled={busy || !slots.length} data-testid="schedule-pdf-print-btn"><Printer className="mr-1.5 h-4 w-4" />Imprimir</Button>
            <Button variant="outline" onClick={download} data-testid="schedule-template-btn"><FileDown className="mr-1.5 h-4 w-4" />Modelo Excel</Button>
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium hover:bg-slate-50" data-testid="schedule-upload-label">
              <UploadCloud className="h-4 w-4" />Enviar planilha
              <input type="file" accept=".xlsx,.csv" className="hidden" onChange={upload} disabled={busy} data-testid="schedule-file-input" />
            </label>
            <Button onClick={save} disabled={busy || !dirty} className="bg-orange-500 hover:bg-orange-600" data-testid="schedule-save-btn">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Salvar
            </Button>
          </div>
        )}
      </Card>
      {ctx?.can_edit && <SchedulePending schoolId={selectedSchoolId} refreshKey={statusKey} onPick={(id) => setParams({ turma: id })} />}
      {warnings.length > 0 && (
        <Card className="mb-4 border-amber-200 bg-amber-50 p-4" data-testid="schedule-warnings">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" />Alertas ({warnings.length})</div>
          <ul className="list-disc pl-5 text-xs text-amber-800">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </Card>
      )}
      {!classes.length ? <EmptyState icon={CalendarClock} title="Nenhuma turma" description="Cadastre turmas para montar o quadro de horários." />
        : !view ? <p className="py-10 text-center text-sm text-slate-400">Carregando...</p>
        : (
          <Card className="min-w-0 p-3 sm:p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <Badge className="bg-slate-800">Turma {view.class.name}</Badge>
              <Badge variant="outline">{shifts.find((s) => s.key === shift)?.label}</Badge>
              {dirty && <Badge className="bg-amber-500" data-testid="schedule-dirty">Alterações não salvas</Badge>}
              {ctx.updated_at && <span>Atualizado por {ctx.updated_by} em {new Date(ctx.updated_at).toLocaleString("pt-BR")}</span>}
              {ctx.can_edit && <span className="flex items-center gap-1"><Pencil className="h-3 w-3" />Clique em um tempo para preencher</span>}
            </div>
            <Grid ctx={view} slots={slots} canEdit={ctx.can_edit} onEdit={setEditing} />
            <p className="mt-3 text-xs text-slate-500">Presença pelo quiosque: chegada até 15 min após o início do tempo = presença; depois disso = falta naquele tempo e presença a partir do tempo seguinte. Sem chegada no dia = falta em todos os tempos.</p>
          </Card>
        )}
      <SlotDialog slot={editing} ctx={view || { teachers: [], subjects: [], periods: [], days: [] }} onClose={() => setEditing(null)} onApply={apply} />
    </div>
  );
}
